/**
 * PricingIntelligence — competitive market context panel.
 *
 * Shows where your price sits vs. the market, surfaces a recommended action,
 * and lets you apply a strategy preset in one click.
 */

import { useMemo } from "react";
import {
  TrendingUp, TrendingDown, Minus, Trophy, Target,
  Zap, Shield, ExternalLink,
} from "lucide-react";
import { CompetitorPrice } from "@/lib/api";

interface Props {
  livePrice: number;
  cost: number;
  floor: number;
  competitors: CompetitorPrice[];
  scanning?: boolean;
  onApplyPrice: (price: number) => void;
}

function fmt(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function pct(a: number, b: number) {
  if (b === 0) return 0;
  return ((a - b) / b) * 100;
}

const SHOPIFY_RATE = 0.029;
const SHOPIFY_FIXED = 0.30;
function netMargin(price: number, cost: number) {
  const fee = price * SHOPIFY_RATE + SHOPIFY_FIXED;
  return price > 0 ? ((price - cost - fee) / price) * 100 : 0;
}

export function PricingIntelligence({ livePrice, cost, floor, competitors, scanning, onApplyPrice }: Props) {
  const prices = useMemo(() => competitors.map(c => c.price).filter(p => p > 0), [competitors]);

  const marketAvg = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
  const marketMin = prices.length > 0 ? Math.min(...prices) : 0;
  const marketMax = prices.length > 0 ? Math.max(...prices) : 0;
  const allPrices = prices.length > 0 ? [...prices, livePrice].sort((a, b) => a - b) : [];
  const rank = allPrices.indexOf(livePrice) + 1; // 1 = cheapest

  const gapToAvg = pct(livePrice, marketAvg);
  const gapToMin = pct(livePrice, marketMin);

  // Margin at various reference prices
  const marginAtLive = netMargin(livePrice, cost);
  const marginAtAvg = marketAvg > 0 ? netMargin(marketAvg, cost) : 0;


  // Recommended beat price: cheapest competitor minus 1 cent, clamped to floor
  const beatPrice = marketMin > 0 ? Math.max(Math.floor((marketMin - 0.01) * 100) / 100, floor) : 0;
  const beatMargin = beatPrice > 0 ? netMargin(beatPrice, cost) : 0;

  // Strategy presets
  const strategies = [
    {
      id: "win",
      label: "Win Rate",
      icon: Trophy,
      description: "Beat cheapest competitor",
      price: beatPrice || livePrice,
      margin: beatMargin,
      color: "text-emerald-500",
      disabled: beatPrice <= 0 || beatPrice < floor,
    },
    {
      id: "balanced",
      label: "Balanced",
      icon: Target,
      description: "Match market average",
      price: Math.max(marketAvg > 0 ? Math.round(marketAvg * 100) / 100 : livePrice, floor),
      margin: marketAvg > 0 ? netMargin(Math.max(marketAvg, floor), cost) : marginAtLive,
      color: "text-blue-500",
      disabled: marketAvg <= 0,
    },
    {
      id: "margin",
      label: "Margin Max",
      icon: Shield,
      description: "Price near market high",
      price: Math.max(marketMax > 0 ? Math.round(marketMax * 0.98 * 100) / 100 : livePrice, floor),
      margin: marketMax > 0 ? netMargin(Math.round(marketMax * 0.98 * 100) / 100, cost) : marginAtLive,
      color: "text-violet-500",
      disabled: marketMax <= 0,
    },
  ];

  if (prices.length === 0) {
    if (scanning) {
      return (
        <div className="space-y-2">
          <div className="h-20 rounded-lg bg-muted/40 animate-pulse" />
          <div className="h-32 rounded-lg bg-muted/30 animate-pulse" />
          <div className="h-16 rounded-lg bg-muted/20 animate-pulse" />
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-5 text-center text-xs text-muted-foreground">
        No competitor data — click <span className="font-medium text-foreground">Analyze with AI</span> to scan the market.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Market position header */}
      <div className="grid grid-cols-3 gap-3">
        {/* Rank */}
        <div className="rounded-lg bg-muted/50 border border-border p-3 text-center">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Your rank</div>
          <div className={`text-2xl font-bold tabular-nums ${rank === 1 ? "text-emerald-500" : rank <= 2 ? "text-amber-500" : "text-rose-500"}`}>
            #{rank}
          </div>
          <div className="text-[10px] text-muted-foreground">of {allPrices.length}</div>
        </div>

        {/* Gap to avg */}
        <div className="rounded-lg bg-muted/50 border border-border p-3 text-center">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">vs. market avg</div>
          <div className={`text-2xl font-bold tabular-nums flex items-center justify-center gap-1 ${
            gapToAvg > 5 ? "text-rose-500" : gapToAvg < -5 ? "text-emerald-500" : "text-amber-500"
          }`}>
            {gapToAvg > 0.5 ? <TrendingUp className="h-4 w-4" /> : gapToAvg < -0.5 ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            {gapToAvg > 0 ? "+" : ""}{gapToAvg.toFixed(1)}%
          </div>
          <div className="text-[10px] text-muted-foreground">{fmt(marketAvg)} avg</div>
        </div>

        {/* Net margin */}
        <div className="rounded-lg bg-muted/50 border border-border p-3 text-center">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Net margin</div>
          <div className={`text-2xl font-bold tabular-nums ${marginAtLive < 5 ? "text-rose-500" : marginAtLive < 15 ? "text-amber-500" : "text-emerald-500"}`}>
            {marginAtLive.toFixed(1)}%
          </div>
          <div className="text-[10px] text-muted-foreground">after Shopify fee</div>
        </div>
      </div>

      {/* Visual price bar chart */}
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Price comparison</div>
        {(() => {
          const chartMin = Math.min(...prices, livePrice) * 0.97;
          const chartMax = Math.max(...prices, livePrice) * 1.03;
          const chartRange = chartMax - chartMin;
          const barWidth = (p: number) => chartRange > 0 ? ((p - chartMin) / chartRange) * 100 : 50;

          const rows: { name: string; price: number; isYou: boolean; url?: string }[] = [
            { name: "You", price: livePrice, isYou: true },
            ...competitors.map(c => ({ name: c.name, price: c.price, isYou: false, url: c.url })),
          ].sort((a, b) => a.price - b.price);

          return rows.map((row, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <div className="w-20 truncate text-right text-muted-foreground shrink-0">
                {row.isYou ? <span className="font-semibold text-foreground">You</span> : row.name}
              </div>
              <div className="flex-1 relative h-5 rounded overflow-hidden bg-muted/40">
                <div
                  className={`absolute left-0 top-0 bottom-0 rounded transition-all ${
                    row.isYou
                      ? "bg-emerald-500/80"
                      : row.price < livePrice
                      ? "bg-rose-500/60"
                      : "bg-slate-400/40"
                  }`}
                  style={{ width: `${barWidth(row.price)}%` }}
                />
                <span className="absolute inset-0 flex items-center pl-2 font-mono tabular-nums text-[10px] font-medium">
                  {fmt(row.price)}
                </span>
              </div>
              {!row.isYou && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[10px] tabular-nums ${row.price < livePrice ? "text-rose-500" : "text-emerald-500"}`}>
                    {row.price < livePrice ? "−" : "+"}{Math.abs(pct(livePrice, row.price)).toFixed(1)}%
                  </span>
                  {row.url && (
                    <a href={row.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <button
                    onClick={() => onApplyPrice(Math.max(Math.floor((row.price - 0.01) * 100) / 100, floor))}
                    className="text-[10px] text-muted-foreground hover:text-emerald-500 px-1.5 py-0.5 rounded border border-border hover:border-emerald-500/50 transition-colors"
                    title={`Beat ${row.name} by $0.01`}
                  >
                    Beat
                  </button>
                </div>
              )}
            </div>
          ));
        })()}
      </div>

      {/* Market reference line */}
      {marketAvg > 0 && (
        <div className="text-[10px] text-muted-foreground flex gap-4">
          <span>Market min: <span className="font-mono text-rose-400">{fmt(marketMin)}</span></span>
          <span>Market avg: <span className="font-mono text-amber-400">{fmt(marketAvg)}</span></span>
          <span>Market max: <span className="font-mono text-emerald-400">{fmt(marketMax)}</span></span>
        </div>
      )}

      {/* Strategy presets */}
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Quick strategies</div>
        <div className="grid grid-cols-3 gap-2">
          {strategies.map(s => (
            <button
              key={s.id}
              onClick={() => !s.disabled && onApplyPrice(s.price)}
              disabled={s.disabled}
              className={`rounded-lg border border-border bg-muted/30 p-2.5 text-left transition-all hover:border-current hover:bg-muted/60 disabled:opacity-40 disabled:cursor-not-allowed ${s.color}`}
            >
              <s.icon className="h-3.5 w-3.5 mb-1" />
              <div className="text-xs font-semibold">{s.label}</div>
              <div className="text-[10px] text-muted-foreground">{s.description}</div>
              {!s.disabled && (
                <div className="mt-1.5 font-mono tabular-nums text-[10px] font-bold">
                  {fmt(s.price)}
                  <span className="ml-1 text-muted-foreground">({s.margin.toFixed(1)}% net)</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Insight callout */}
      {(() => {
        if (gapToMin > 10) {
          return (
            <div className="flex items-start gap-2 rounded-md bg-rose-500/10 border border-rose-500/20 px-3 py-2 text-xs text-rose-400">
              <Zap className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                You're <strong>{gapToMin.toFixed(1)}% above the cheapest competitor</strong>. Price above {fmt(beatPrice)} risks losing the sale unless your margin justifies it.
              </span>
            </div>
          );
        }
        if (gapToMin < -5) {
          return (
            <div className="flex items-start gap-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 text-xs text-emerald-400">
              <Trophy className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                You're the <strong>cheapest by {Math.abs(gapToMin).toFixed(1)}%</strong>. Consider raising price toward market average ({fmt(marketAvg)}) for +{(marginAtAvg - marginAtLive).toFixed(1)}% net margin uplift.
              </span>
            </div>
          );
        }
        if (marginAtLive < 8) {
          return (
            <div className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs text-amber-400">
              <Shield className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Net margin of <strong>{marginAtLive.toFixed(1)}%</strong> is thin after Shopify fees. Pricing at market avg ({fmt(marketAvg)}) improves it to <strong>{marginAtAvg.toFixed(1)}%</strong>.
              </span>
            </div>
          );
        }
        return (
          <div className="flex items-start gap-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 text-xs text-emerald-400">
            <Target className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Competitive position looks good — ranked #{rank} at {fmt(livePrice)} with {marginAtLive.toFixed(1)}% net margin.
            </span>
          </div>
        );
      })()}
    </div>
  );
}
